import { useReducer } from 'react';
import { WizardStep } from '../../types/WizardSteps';
import { HistoryStep, Step, Phase } from './WizardMultistepTypes';

export type WizardMultistepReducerState = {
    steps: { [key in WizardStep]?: Step };
    stepsHistory: Array<HistoryStep>;
    currentStepId: WizardStep | null;
    initialStepId: WizardStep | null;
    phases: Array<Phase>;
};

type Action =
    | {
          type: 'JUMP_TO_STEP';
          payload: { id: WizardStep };
      }
    | {
          type: 'REGISTER_STEP';
          payload: Partial<Step> & {
              id: WizardStep;
          };
      }
    | {
          type: 'MODIFY_STEP';
          payload: Partial<Step> & {
              id: WizardStep;
          };
      }
    | {
          type: 'MODIFY_MULTIPLE_STEPS';
          payload: {
              steps: {
                  [key in WizardStep]?: Partial<Step>;
              };
          };
      }
    | {
          type: 'MODIFY_PHASE';
          payload: {
              index: number;
              data: Partial<Phase>;
          };
      };

const INITIAL_STATE: WizardMultistepReducerState = {
    steps: {},
    stepsHistory: [],
    initialStepId: null,
    currentStepId: null,
    phases: [],
};

function wizardMultistepReducer(state: WizardMultistepReducerState, action: Action): WizardMultistepReducerState {
    switch (action.type) {
        case 'REGISTER_STEP': {
            if (state.steps[action.payload.id]) {
                // step already registered
                return state;
            }
            return {
                ...state,
                steps: {
                    ...state.steps,
                    [action.payload.id]: {
                        completed: false,
                        skipped: false,
                        nextStepId: null,
                        skippable: false,
                        ...action.payload,
                    },
                },
            };
        }
        case 'MODIFY_STEP': {
            return {
                ...state,
                steps: {
                    ...state.steps,
                    [action.payload.id]: { ...state.steps[action.payload.id], ...action.payload },
                },
            };
        }
        case 'MODIFY_MULTIPLE_STEPS': {
            const nextSteps = { ...state.steps };
            Object.entries(action.payload.steps).forEach(([id, modifications]: [unknown, Partial<Step>]) => {
                const step = nextSteps[id as WizardStep];
                if (step) {
                    nextSteps[id as WizardStep] = {
                        ...step,
                        ...modifications,
                    };
                }
            });
            return {
                ...state,
                steps: nextSteps,
            };
        }
        case 'JUMP_TO_STEP': {
            return {
                ...state,
                initialStepId: state.initialStepId ? state.initialStepId : action.payload.id,
                currentStepId: action.payload.id,
            };
        }
        case 'MODIFY_PHASE': {
            if (action.payload.index > state.phases.length - 1) {
                return state;
            }
            const nextPhases = [...state.phases];
            nextPhases[action.payload.index] = {
                ...nextPhases[action.payload.index],
                ...action.payload.data,
            };
            return {
                ...state,
                phases: nextPhases,
            };
        }
        default: {
            return state;
        }
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useWizardMultistepReducer = (initial: Partial<WizardMultistepReducerState>) =>
    useReducer(wizardMultistepReducer, { ...INITIAL_STATE, ...initial });
